import type { ApiKey, Qubic, Subscription } from "api";
import { QUOTE_URL } from "constants/common.constants";
import { cubeUrl, findProductByTool, METERED_PRODUCTS } from "constants/products.constants";
import { formatTimestamp } from "utils/date.utils";
import { formatCount } from "utils/number.utils";

/** One entry of the create row's product picker. */
export type KeyProduct = {
  serviceTool: string;
  name: string;
  cube: string;
};

/**
 * Products a key can be issued against: the API catalogue entries that stand
 * for exactly one gateway tool, as the back office asks. Global Tracking is two
 * tools and the unmetered products are none, so neither can carry a key. Until
 * the catalogue arrives the picker falls back to the built-in product list, so
 * the form never opens empty.
 */
export const keyProducts = (qubics: Qubic[]): KeyProduct[] => {
  const fromCatalogue = qubics
    .filter((qubic) => qubic.kinds.includes("api") && qubic.service_tools.length === 1)
    .map((qubic) => ({ serviceTool: qubic.service_tools[0], name: qubic.name, cube: cubeUrl(qubic.cube) }));

  if (fromCatalogue.length > 0) return fromCatalogue;

  return METERED_PRODUCTS.map((product) => ({
    serviceTool: product.serviceTool as string,
    name: product.name,
    cube: product.cube,
  }));
};

export type KeyRow = {
  /** Null on a key issued before keys were bound to a product. */
  cube: string | null;
  tag: { label: string; modifier: string | null } | null;
  note: string;
  /** Set when the key's product has no calls and no limit: calls have to be requested. */
  quoteUrl: string | null;
};

/**
 * What a `.pc-key` row says. There is no "last used": `last_used_at` never
 * leaves null. The call count and the Active / Not used yet tag are the
 * product's, not the key's — the gateway counts per company and tool.
 */
export const keyRow = (key: ApiKey, qubics: Qubic[], subscriptions: Subscription[]): KeyRow => {
  const qubic = qubics.find((entry) => entry.slug === key.product);
  const fallback = findProductByTool(key.service_tool);
  const calls = key.calls_this_month;
  const limited = subscriptions.some(
    (subscription) => subscription.service_tool === key.service_tool && subscription.monthly_limit !== null
  );
  const unrequested = calls === 0 && !limited;

  let callsFact: string | null = null;
  if (unrequested) {
    callsFact = "calls have to be requested";
  } else if (calls !== null) {
    callsFact = `${formatCount(calls)} calls on this product this month`;
  }

  const facts = [
    qubic?.name ?? fallback?.name ?? key.service_tool_label,
    `created ${formatTimestamp(key.created_at)}`,
    callsFact,
  ].filter(Boolean);

  let tag: KeyRow["tag"] = null;
  if (calls !== null) {
    tag = calls > 0 ? { label: "Active", modifier: "pc-tag--ok" } : { label: "Not used yet", modifier: null };
  }

  return {
    cube: qubic ? cubeUrl(qubic.cube) : (fallback?.cube ?? null),
    tag,
    note: facts.join(" · "),
    quoteUrl:
      unrequested && key.service_tool !== null
        ? `${QUOTE_URL}?serviceTool=${encodeURIComponent(key.service_tool)}`
        : null,
  };
};
